import { type ListHistogramResult } from '@solink/vd-data-layer/api-clients/platform/platform';
import {
  EVENT_TYPE_BOOKMARK,
  EVENT_TYPE_MOTION,
  EVENT_TYPE_TRANSACTION,
  type HistogramRange,
} from '@solinkcorp/vd-common';
import { type Immutable } from 'immer';
import moment from 'moment-timezone';
import { makeImmerReducer } from './utils';

export type EventHistogramRange = Exclude<HistogramRange, 'Hour'>;

// This comes from processHistogramData() in mutators.js.
export interface ComboChartData {
  bookmarks: { count: number; totalAmountSum: number; type: 'bookmark' };
  motions: { count: number; totalAmountSum: number; type: 'motion' };
  transactions: { count: number; totalAmountSum: number; type: 'transaction' };
  meta: {
    count: number;
    fieldStats: unknown;
    totalAmountSum: number;
    type: 'transaction' | 'motion' | 'bookmark';
  }[];
  startTime: number;
  endTime: number;
  title: number;
  total: number;
}

export interface HistogramData {
  readonly current: Immutable<ComboChartData[]>;
  readonly previous: Immutable<ComboChartData[]>;
}

/**
 * Reducer which keeps track of the data for the histogram on the events page.
 */
export type HistogramState = {
  data: {
    current: ComboChartData[];
    previous: ComboChartData[];
  };
  fetching: boolean;
  error: boolean;
};

export const initialState: HistogramState = {
  data: { current: [], previous: [] },
  fetching: false,
  error: false,
};

export const histogramReducer = makeImmerReducer(initialState, (draft, action) => {
  switch (action.type) {
    case 'FETCH_HISTOGRAM':
      draft.fetching = true;
      break;

    case 'FETCH_HISTOGRAM_FETCHED': {
      draft.fetching = false;
      const { prevRangeData, data, timezoneId } = action;

      const previous = prevRangeData ? processHistogramData(prevRangeData, timezoneId) : [];
      const current = processHistogramData(data, timezoneId);

      draft.data = { current, previous };
      break;
    }

    case 'FETCH_HISTOGRAM_ERROR':
      draft.error = true;
      break;
  }
});

const processHistogramData = (
  data: ListHistogramResult['results'],
  timeZoneId: string,
): ComboChartData[] => {
  return data.map((res, idx) => {
    const { startTime, endTime, total, results } = res;
    const defaultValues = { count: 0, totalAmountSum: 0 };

    const processed: ComboChartData = {
      startTime: moment.tz(startTime, timeZoneId).valueOf(),
      endTime: moment.tz(endTime, timeZoneId).valueOf(),
      title: idx,
      total,
      meta: results,
      transactions: { type: EVENT_TYPE_TRANSACTION, ...defaultValues },
      motions: { type: EVENT_TYPE_MOTION, ...defaultValues },
      bookmarks: { type: EVENT_TYPE_BOOKMARK, ...defaultValues },
    };

    for (const { type, count, totalAmountSum } of results) {
      switch (type) {
        case EVENT_TYPE_TRANSACTION:
          processed.transactions = { type, count, totalAmountSum };
          break;
        case EVENT_TYPE_MOTION:
          processed.motions = { type, count, totalAmountSum };
          break;
        case EVENT_TYPE_BOOKMARK:
          processed.bookmarks = { type, count, totalAmountSum };
          break;
        default:
          throw new Error(`Unknown type: ${type}`);
      }
    }

    return processed;
  });
};
